import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  DashboardIcon,
  ChatIcon,
  ActivityIcon,
  EventsIcon,
  CollaborationIcon,
  SettingsIcon,
  PanelLeftIcon,
  ChevronDownIcon,
  RevenueSignalsIcon,
  SoftwareIcon,
} from './icons.jsx'
import { CATEGORIES, categoryPath } from '../lib/categories.js'
import { useRbac } from '../lib/rbac.js'

// Grouped nav config — sections render as labeled groups, matching the design.
const SECTIONS = [
  {
    title: 'General',
    items: [
      // A member's home is chat — the dashboard row (like the whole leadership
      // cockpit below) is gated on pages.workspace.
      { to: '/dashboard', label: 'Dashboard', icon: DashboardIcon, end: true, perm: 'pages.workspace' },
      { to: '/chat', label: 'Chat', icon: ChatIcon },
    ],
  },
  {
    title: 'Leads',
    items: [
      // One nav item per vertical in lib/categories.js.
      ...CATEGORIES.map((c) => ({ to: categoryPath(c.key), label: c.label, icon: c.icon })),
    ],
  },
  {
    title: 'Performance',
    perm: 'pages.workspace',
    items: [
      { to: '/performance/activity', label: 'Activity', icon: ActivityIcon },
      // Sales is a sample page until Stripe is wired (revenue, subscriptions,
      // payments) and says "Sample data" on the page itself, so its row is not
      // a claim that the numbers are live. Software IS live: production health
      // read from the workloads-production account (lib/softwareHealth.js).
      { to: '/performance/sales', label: 'Sales', icon: RevenueSignalsIcon },
      { to: '/performance/software', label: 'Software', icon: SoftwareIcon },
      // Events used to be a 'Logs' section of its own. It is the raw feed
      // behind the same question Activity charts, so it reads better under this
      // heading than as a one-row section below it. Two things survive the
      // move. The route stays EXACTLY '/logs/events': those links are
      // bookmarked, pasted into decks and mailed, and silently re-pointing a
      // shared link is a bug in this codebase, not a tidy-up. And nobody loses
      // the row by it — the audit log is admin-only and `perm` still hides it
      // for everyone else, but events.view is ADMINS (owner+admin) while this
      // section's own pages.workspace is owner, admin, manager and viewer, a
      // strict superset (verified in src/lib/rbac.js), so every role that could
      // reach Events before still passes both gates.
      { to: '/logs/events', label: 'Events', icon: EventsIcon, perm: 'events.view' },
    ],
  },
]

const FOOTER_ITEMS = [
  { to: '/collaboration', label: 'Collaboration', icon: CollaborationIcon },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
]

// Sections that start collapsed on first load. (They still auto-open when they
// contain the active route, so deep links never hide the current page.) None
// today: Performance opens like the rest (owner, 2026-10-11: "performance
// should be automatically dropped down").
const COLLAPSED_BY_DEFAULT = new Set()

function matchesPath(pathname, to, end) {
  if (!to) return false
  return end ? pathname === to : pathname === to || pathname.startsWith(to + '/')
}

// The active row's background cross-fades in/out via `transition-colors`.
function NavItem({ to, label, icon: IconEl, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) =>
        `mb-[2px] flex items-center gap-x-3 rounded-[8px] px-[10px] py-[9px] text-[13.5px] no-underline transition-colors duration-200 [&_svg]:h-[17px] [&_svg]:w-[17px] [&_svg]:[stroke-width:1.5px] [&_svg]:flex-none [&_svg]:transition-colors [&_svg]:duration-200 group-[.sidebar-collapsed]:justify-center group-[.sidebar-collapsed]:gap-x-0 group-[.sidebar-collapsed]:px-0 group-[.sidebar-collapsed]:[&_span]:hidden ${
          isActive
            ? 'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.09)] dark:shadow-none font-medium text-[#18181b] [&_svg]:text-[#18181b] dark:bg-[#242424] dark:text-[#f5f5f5] dark:[&_svg]:text-[#f5f5f5]'
            : 'font-normal text-[#44403c] hover:bg-[#e6e7ec] [&_svg]:text-[#78716c] dark:text-[#d4d4d4] dark:hover:bg-[#242424]'
        }`
      }
    >
      <IconEl />
      <span>{label}</span>
    </NavLink>
  )
}

// Parent item with a collapsible submenu of child links. Opens automatically
// when one of its children matches the current route. Clicking it while none of
// its children are active selects the first child; otherwise it just toggles.
function NavGroup({ label, icon: IconEl, items }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const activeIndex = items.findIndex((c) => matchesPath(pathname, c.to, c.end))
  const childActive = activeIndex !== -1
  const [open, setOpen] = useState(childActive)
  const listRef = useRef(null)
  // A single shared indicator that slides between subitems, so moving the
  // selection animates instead of the per-item bar snapping on/off.
  const [indicator, setIndicator] = useState({ top: 0, height: 0, visible: false })

  useEffect(() => {
    if (childActive) setOpen(true)
  }, [childActive])

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || activeIndex === -1) {
      setIndicator((i) => ({ ...i, visible: false }))
      return
    }
    const el = list.querySelectorAll('a')[activeIndex]
    if (!el) return
    // Match the inset look of the old per-item bar (6px in from top/bottom).
    setIndicator({ top: el.offsetTop + 6, height: Math.max(el.offsetHeight - 12, 0), visible: true })
  }, [activeIndex, open, pathname])

  return (
    <div>
      <button
        type="button"
        className={`mb-[2px] flex w-full items-center gap-x-3 rounded-[8px] border-none px-[10px] py-[9px] text-left text-[13.5px] font-normal no-underline transition-colors duration-200 [&_svg]:h-[17px] [&_svg]:w-[17px] [&_svg]:[stroke-width:1.5px] [&_svg]:flex-none [&_svg]:transition-colors [&_svg]:duration-200 group-[.sidebar-collapsed]:justify-center group-[.sidebar-collapsed]:gap-x-0 group-[.sidebar-collapsed]:px-0 group-[.sidebar-collapsed]:[&_span]:hidden ${
          childActive
            ? 'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.09)] dark:shadow-none text-[#18181b] [&_svg]:text-[#18181b] dark:bg-[#242424] dark:text-[#f5f5f5] dark:[&_svg]:text-[#f5f5f5]'
            : 'bg-transparent text-[#44403c] [&_svg]:text-[#78716c] hover:bg-[#e6e7ec] dark:text-[#d4d4d4] dark:hover:bg-[#242424]'
        }`}
        onClick={() => {
          if (!childActive) {
            navigate(items[0].to)
            setOpen(true)
          } else {
            setOpen((o) => !o)
          }
        }}
        aria-expanded={open}
        title={label}
      >
        <IconEl />
        <span>{label}</span>
        <ChevronDownIcon
          className={`ml-auto group-[.sidebar-collapsed]:hidden ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-[240ms] group-[.sidebar-collapsed]:transition-none ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        } group-[.sidebar-collapsed]:grid-rows-[1fr]`}
      >
        {/* py-px only while open: it gives the active row's shadow 1px of room,
            but any vertical padding survives the 0fr collapse and shows as a
            sliver of the highlighted first row. Content also fades on toggle. */}
        <div
          className={`-mx-1 min-h-0 overflow-hidden px-1 transition-opacity duration-[240ms] group-[.sidebar-collapsed]:opacity-100 group-[.sidebar-collapsed]:transition-none ${
            open ? 'py-px opacity-100' : 'py-0 opacity-0'
          }`}
        >
          <div
            ref={listRef}
            className="relative mb-1 mt-[2px] pl-[30px] before:absolute before:bottom-[2px] before:left-[19px] before:top-[2px] before:w-px before:bg-[#e2e2e5] before:content-[''] dark:before:bg-[#333333] group-[.sidebar-collapsed]:hidden"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-[19px] top-0 w-[2px] rounded-[2px] bg-[#18181b] shadow-[0_0_8px_rgba(24,24,27,0.45)] transition-[transform,height,opacity] duration-[220ms] ease-out dark:bg-[#f5f5f5] dark:shadow-[0_0_10px_rgba(245,245,245,0.55)]"
              style={{
                transform: `translateY(${indicator.top}px)`,
                height: `${indicator.height}px`,
                opacity: indicator.visible ? 1 : 0,
              }}
            />
            {items.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                end={c.end}
                className={({ isActive }) =>
                  `relative block rounded-[8px] px-[10px] py-2 text-[13px] no-underline transition-[color,text-shadow] duration-200 hover:bg-[#e6e7ec] dark:hover:bg-[#242424] ${
                    isActive
                      ? 'font-medium text-[#18181b] [text-shadow:0_0_10px_rgba(24,24,27,0.28)] dark:text-[#f5f5f5] dark:[text-shadow:0_0_12px_rgba(245,245,245,0.45)]'
                      : 'font-normal text-[#78716c] hover:text-[#44403c] dark:text-[#a3a3a3] dark:hover:text-[#e5e5e5]'
                  }`
                }
              >
                <span>{c.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// A labeled nav group whose items collapse under a clickable title. Starts
// open unless `defaultOpen` is false, and auto-opens when it holds the active
// route so the current page is never hidden. Clicking the title while the
// section holds no active route selects its first page; otherwise it toggles.
function NavSection({ title, items, defaultOpen }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const sectionActive = items.some((item) => {
    const targets = item.children
      ? item.children.map((c) => ({ to: c.to, end: c.end }))
      : [{ to: item.to, end: item.end }]
    return targets.some((t) => matchesPath(pathname, t.to, t.end))
  })
  const [open, setOpen] = useState(defaultOpen || sectionActive)
  const firstTo = items[0]?.to || items[0]?.children?.[0]?.to

  useEffect(() => {
    if (sectionActive) setOpen(true)
  }, [sectionActive])

  return (
    <div className="mt-4 first:mt-1 group-[.sidebar-collapsed]:mt-2">
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-x-[6px] border-none bg-transparent px-[10px] pb-[6px] pt-1 text-[11px] font-semibold uppercase tracking-[0.03em] text-[#a8a29e] transition-colors duration-[120ms] hover:text-[#78716c] group-[.sidebar-collapsed]:hidden"
        onClick={() => {
          if (!sectionActive && firstTo) {
            navigate(firstTo)
            setOpen(true)
          } else {
            setOpen((o) => !o)
          }
        }}
        aria-expanded={open}
        title={title}
      >
        <span>{title}</span>
        <ChevronDownIcon
          className={`ml-auto h-[14px] w-[14px] text-[#a8a29e] transition-transform duration-150 ${
            open ? '' : '-rotate-90'
          }`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-[240ms] group-[.sidebar-collapsed]:transition-none ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        } group-[.sidebar-collapsed]:grid-rows-[1fr]`}
      >
        {/* Same open-only py-px + fade as NavGroup — closed padding would peek
            as a sliver of a highlighted first item. */}
        <div
          className={`-mx-1 min-h-0 overflow-hidden px-1 transition-opacity duration-[240ms] group-[.sidebar-collapsed]:opacity-100 group-[.sidebar-collapsed]:transition-none ${
            open ? 'py-px opacity-100' : 'py-0 opacity-0'
          }`}
        >
          <div>
            {items.map((item) =>
              item.children ? (
                <NavGroup
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  items={item.children}
                />
              ) : (
                <NavItem key={item.to} {...item} />
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const { can, ready } = useRbac()
  // A member's sidebar starts COLLAPSED — their whole workspace is chat and
  // leads, and a rail of icons carries that with less mental load than an open
  // tree. Applied once, when the role actually resolves (roles paint
  // optimistically before the roster answers), and never over a manual toggle:
  // a member who opened the rail meant to.
  const memberView = ready && !can('pages.workspace')
  const userToggledRef = useRef(false)
  useEffect(() => {
    if (memberView && !userToggledRef.current) setCollapsed(true)
  }, [memberView])
  // Sections gate as a whole (perm on the section), then item by item; a
  // section with nothing left to show disappears rather than render a header
  // over an empty list.
  const sections = SECTIONS.filter((s) => !s.perm || can(s.perm))
    .map((s) => ({
      ...s,
      items: s.items.filter((item) => !item.perm || can(item.perm)),
    }))
    .filter((s) => s.items.length > 0)
  // Where the app-name click goes: the cockpit for leadership, chat for a
  // member — the same rule the routes enforce.
  const home = can('pages.workspace') ? '/dashboard' : '/chat'

  return (
    <aside
      className={`sidebar group flex h-full flex-none flex-col bg-transparent transition-[width] duration-[180ms] ${
        collapsed ? 'sidebar-collapsed w-[68px]' : 'w-[240px] max-[860px]:w-[208px]'
      }`}
    >
      {/* App name — clicking it goes home (cockpit or chat, by role). */}
      <div className="flex items-center gap-x-1 px-3 pb-2 pt-3 group-[.sidebar-collapsed]:justify-center group-[.sidebar-collapsed]:px-0">
        <NavLink
          to={home}
          title="Revailo Sales"
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-x-2 rounded-[7px] px-1.5 py-1 text-left no-underline transition-colors hover:bg-[#e6e7ec] dark:hover:bg-[#242424] group-[.sidebar-collapsed]:hidden"
        >
          <span className="grid h-[24px] w-[24px] flex-none place-items-center">
            <img src="/logo-on-light.png?v=5" alt="" className="h-[18px] w-[18px] dark:hidden" />
            <img src="/logo-on-dark.png?v=5" alt="" className="hidden h-[18px] w-[18px] dark:block" />
          </span>
          <span className="truncate text-[14px] font-semibold text-[#1c1917] dark:text-[#f5f5f5]">Revailo Sales</span>
        </NavLink>
        <button
          className="inline-flex flex-none rounded-[6px] border-none bg-transparent p-1.5 text-[#a8a29e] transition-colors duration-200 hover:bg-[#e6e7ec] hover:text-[#57534e] dark:hover:bg-[#242424] dark:hover:text-ink [&_svg]:h-[16px] [&_svg]:w-[16px]"
          type="button"
          onClick={() => {
            userToggledRef.current = true
            setCollapsed((c) => !c)
          }}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <PanelLeftIcon />
        </button>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-16 pt-[6px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden group-[.sidebar-collapsed]:px-[10px]">
        {sections.map((section) => (
          <NavSection
            key={section.title}
            {...section}
            defaultOpen={!COLLAPSED_BY_DEFAULT.has(section.title)}
          />
        ))}
      </nav>

      <div className="px-3 py-[10px] [border-top:1px_solid_rgba(15,23,42,0.06)] dark:[border-top:1px_solid_rgba(255,255,255,0.06)] group-[.sidebar-collapsed]:px-[10px]">
        {FOOTER_ITEMS.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
      </div>
    </aside>
  )
}
